import React from 'react';
import { cva } from 'class-variance-authority';
import styles from './Label.module.css';
import {
  foregroundColorVariants,
  type ForegroundVariants,
} from '../../utils/foregroundColour/foregroundColor';
import {
  getBaseValue,
  generateResponsiveClasses,
  type WithResponsiveProps,
} from '../../utils/breakpoint/responsiveSSR';
import { altoConsole } from '../../utils/console/console';

const deprecatedLabelSizeWarning = () =>
  altoConsole.warn(
    "The size value '2xs' is deprecated. Please use 'xxs' instead.",
    { scope: 'Label' },
  );

const getResponsiveClasses = generateResponsiveClasses(styles);

const label = cva(styles.label, {
  variants: {
    size: {
      '2xs': styles['size--xxs'],
      xxs: styles['size--xxs'],
      xs: styles['size--xs'],
      sm: styles['size--sm'],
      md: styles['size--md'],
      lg: styles['size--lg'],
    },
    truncate: {
      true: styles['truncate--true'],
      false: styles['truncate--false'],
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

type LabelSize = '2xs' | 'xxs' | 'xs' | 'sm' | 'md' | 'lg';

interface BaseProps
  extends Omit<React.HTMLAttributes<Element>, 'className' | 'style'> {
  /**
   * children
   */
  children: React.ReactNode;
  /**
   * The HTML element to render the label as.
   */
  as?: 'span' | 'div' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * **[Responsive]** The size of the label
   */
  size?: LabelSize;
  /**
   * The foreground color of the label
   */
  foregroundColor?: ForegroundVariants['foregroundColor'];
  /**
   * **[Responsive]** Truncate text with ellipsis
   * @default false
   */
  truncate?: boolean;
}

type ResponsivePropKeys = 'size' | 'truncate';

export type LabelProps = WithResponsiveProps<BaseProps, ResponsivePropKeys>;

export const Label = ({
  children,
  as: Component = 'span',
  size,
  foregroundColor,
  truncate = false,
  ...props
}: LabelProps) => {
  // TODO: Remove deprecation warning for '2xs'
  // Deprecation warning for '2xs' and specify to use 'xxs'
  if (size === '2xs') {
    deprecatedLabelSizeWarning();
  } else if (typeof size === 'object' && size !== null) {
    const hasDeprecatedValue = Object.values(size).includes('2xs');
    if (hasDeprecatedValue) {
      deprecatedLabelSizeWarning();
    }
  }

  const baseSize = getBaseValue(size, 'md');
  const truncateBase = getBaseValue(truncate, false);

  const sizeResp = getResponsiveClasses(size, 'size');
  const truncateResp = getResponsiveClasses(truncate, 'truncate');

  const classNames = [
    label({ size: baseSize, truncate: truncateBase }),
    foregroundColorVariants({ foregroundColor }),
    ...sizeResp,
    ...truncateResp,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Component className={classNames} {...props}>
      {children}
    </Component>
  );
};

export default Label;
